import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CropArt } from "@/components/valley/CropSprite";
import { Tree, Barn, Pond, FenceLine, GrassTuft, Flower } from "@/components/valley/Scenery";
import { plotStage, fmtDuration } from "@/components/valley/stage";

const VW = 1000;
const VH = 680;
const TW = 150;
const TH = 75;
const DEPTH = 16;
const OX = 500;
const OY = 175;

const tileTop = (r, c) => [OX + (c - r) * (TW / 2), OY + (c + r) * (TH / 2)];
export const plotCenter = (i, grid) => {
  const [x, y] = tileTop(Math.floor(i / grid), i % grid);
  return [x, y + TH / 2];
};

const TREE_SPOTS = [[880, 210, 0.9], [940, 360, 0.8], [70, 470, 0.85], [175, 610, 0.95]];
const TUFTS = [[60, 120], [320, 90], [700, 80], [960, 120], [40, 300], [980, 520], [420, 640], [640, 650], [300, 560], [760, 600], [880, 460], [130, 380]];
const FLOWERS = [[350, 120, "#ff8fc7"], [660, 110, "#ffd84d"], [90, 200, "#ffffff"], [930, 280, "#ff8fc7"], [560, 620, "#ffd84d"], [250, 520, "#ffffff"]];

function SoilTile({ x, y, locked, ready, hover }) {
  const top = `${x},${y} ${x + TW / 2},${y + TH / 2} ${x},${y + TH} ${x - TW / 2},${y + TH / 2}`;
  const left = `${x - TW / 2},${y + TH / 2} ${x},${y + TH} ${x},${y + TH + DEPTH} ${x - TW / 2},${y + TH / 2 + DEPTH}`;
  const right = `${x + TW / 2},${y + TH / 2} ${x},${y + TH} ${x},${y + TH + DEPTH} ${x + TW / 2},${y + TH / 2 + DEPTH}`;
  if (locked) {
    return (
      <g>
        <polygon points={top} fill="#4fa83d" stroke={hover ? "#ffe066" : "#3f8f31"} strokeWidth={hover ? 3 : 1.5} filter={hover ? "url(#glow)" : undefined} />
        {[[-28, 6], [0, -8], [24, 10], [-6, 22]].map(([dx, dy]) => (
          <path key={`${dx}${dy}`} d={`M${x + dx} ${y + TH / 2 + dy} q-3 -8 -7 -11 M${x + dx} ${y + TH / 2 + dy} q1 -9 2 -13 M${x + dx} ${y + TH / 2 + dy} q4 -7 8 -9`} stroke="#2f7d24" strokeWidth="2" fill="none" strokeLinecap="round" />
        ))}
      </g>
    );
  }
  return (
    <g>
      <polygon points={left} fill="#5a3a20" />
      <polygon points={right} fill="#462c17" />
      <polygon points={top} fill="url(#soil)" stroke={ready ? "#ffd84d" : hover ? "#ffe066" : "#3d2614"} strokeWidth={ready || hover ? 3 : 1.5}
        filter={ready || hover ? "url(#glow)" : undefined} />
      {[-0.25, 0, 0.25].map((k) => (
        <line key={k} x1={x - TW / 4 + k * TW * 0.5} y1={y + TH / 4 + k * TH * 0.5} x2={x + TW / 4 + k * TW * 0.5} y2={y + (3 * TH) / 4 + k * TH * 0.5}
          stroke="#3b2414" strokeWidth="3" strokeLinecap="round" opacity="0.55" />
      ))}
    </g>
  );
}

function LockedSign({ x, y, cost }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d="M-34 6 L-34 -10 M34 6 L34 -10 M-34 -6 L34 -6 M-34 0 L34 0" stroke="#b07a40" strokeWidth="3.5" strokeLinecap="round" />
      <rect x="-3" y="-42" width="6" height="40" fill="#8b5a2b" />
      <rect x="-26" y="-58" width="52" height="22" rx="3" fill="#d9a066" stroke="#8b5a2b" strokeWidth="2" />
      <text x="0" y="-43" textAnchor="middle" fontSize="11" fontWeight="800" fill="#4a2a10" fontFamily="Fredoka, sans-serif">{`${cost} CRO`}</text>
    </g>
  );
}

function ReadyMarker({ x, y }) {
  return (
    <motion.g animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 1.2 }}>
      <circle cx={x} cy={y} r="13" fill="#ffd84d" filter="url(#glowStrong)" />
      <text x={x} y={y + 6} textAnchor="middle" fontSize="20" fontWeight="900" fill="#5a3400" fontFamily="Fredoka, sans-serif">!</text>
    </motion.g>
  );
}

function Plot({ i, grid, plot, unlocked, now, onClick, unlockCost, hover, setHover }) {
  const [x, y] = tileTop(Math.floor(i / grid), i % grid);
  const stage = plotStage(plot, now);
  const cy = y + TH / 2;
  return (
    <g data-testid={`valley-plot-${i}`} data-stage={unlocked ? stage : "locked"} onClick={() => onClick(i)}
      onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }}>
      <SoilTile x={x} y={y} locked={!unlocked} ready={stage === "ready"} hover={hover} />
      {!unlocked && <LockedSign x={x} y={cy + 8} cost={unlockCost} />}
      {unlocked && plot && (
        <motion.g key={stage} initial={{ scale: 0.3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 240, damping: 14 }} style={{ originX: `${x}px`, originY: `${cy + 10}px` }}>
          <g transform={`translate(${x - 54} ${cy + 12 - 98}) scale(1.8)`}><CropArt seed={plot.seed} stage={stage} /></g>
        </motion.g>
      )}
    </g>
  );
}

function PlotOverlay({ i, grid, plot, unlocked, now, hover }) {
  const [x, y] = tileTop(Math.floor(i / grid), i % grid);
  const stage = plotStage(plot, now);
  const cy = y + TH / 2;
  if (!unlocked) return null;
  if (!plot) {
    return hover ? <text x={x} y={cy + 5} textAnchor="middle" fontSize="14" fontWeight="800" fill="#ffe7b3" fontFamily="Fredoka, sans-serif" pointerEvents="none">PLANT</text> : null;
  }
  const remaining = stage === "ready" ? plot.wither_at - now : plot.ready_at - now;
  const label = stage === "withered" ? "CLEAR" : stage === "ready" ? `HARVEST · ${fmtDuration(remaining)}` : fmtDuration(remaining);
  return (
    <g pointerEvents="none">
      {stage === "ready" && <ReadyMarker x={x} y={cy - 84} />}
      <g data-testid={`valley-plot-timer-${i}`}>
        <rect x={x - 50} y={cy + 18} width="100" height="20" rx="10" fill={stage === "ready" ? "#ffd84d" : stage === "withered" ? "#7f1d1d" : "rgba(0,0,0,0.7)"} />
        <text x={x} y={cy + 32} textAnchor="middle" fontSize="11.5" fontWeight="800" fontFamily="Fredoka, sans-serif" fill={stage === "ready" ? "#3a2300" : "#f8fafc"}>{label}</text>
      </g>
    </g>
  );
}

function Fx({ fx, grid }) {
  const [x, y] = plotCenter(fx.plot, grid);
  if (fx.type === "dust") {
    return (
      <g>
        {Array.from({ length: 8 }).map((_, k) => {
          const a = (k / 8) * Math.PI * 2;
          return (
            <motion.circle key={k} cx={x} cy={y + 6} r="7" fill="#b98a5a"
              initial={{ opacity: 0.85, cx: x, cy: y + 6, r: 5 }}
              animate={{ opacity: 0, cx: x + Math.cos(a) * 46, cy: y + 6 + Math.sin(a) * 20 - 8, r: 13 }}
              transition={{ duration: 0.9, ease: "easeOut" }} />
          );
        })}
      </g>
    );
  }
  if (fx.type === "burst") {
    return (
      <g>
        {Array.from({ length: 14 }).map((_, k) => {
          const a = (k / 14) * Math.PI * 2;
          const coin = k % 2 === 0;
          return (
            <motion.g key={k} initial={{ x: 0, y: 0, opacity: 1, scale: 0.6 }}
              animate={{ x: Math.cos(a) * 70, y: Math.sin(a) * 40 - 60, opacity: 0, scale: 1.2 }}
              transition={{ duration: 1.1, ease: "easeOut" }}>
              {coin ? (
                <g><circle cx={x} cy={y - 30} r="7" fill="#ffc72c" stroke="#d99700" strokeWidth="2" /><text x={x} y={y - 26.5} textAnchor="middle" fontSize="8" fontWeight="900" fill="#8a5a00">F</text></g>
              ) : (
                <path d={`M${x} ${y - 40} l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 l7 -3z`} fill="#fff6a8" />
              )}
            </motion.g>
          );
        })}
      </g>
    );
  }
  return (
    <motion.text x={x} y={y - 60} textAnchor="middle" fontSize="26" fontWeight="900" fontFamily="Fredoka, sans-serif"
      fill={fx.color} stroke="#1a1208" strokeWidth="4" paintOrder="stroke"
      initial={{ y: y - 50, opacity: 1 }} animate={{ y: y - 130, opacity: 0 }} transition={{ duration: 1.5, ease: "easeOut" }}>
      {`${fx.text}`}
    </motion.text>
  );
}

function DecorSpot({ x, y, label, onClick, tid }) {
  return (
    <g onClick={onClick} style={{ cursor: "pointer" }} data-testid={tid} opacity="0.75">
      <ellipse cx={x} cy={y} rx="34" ry="14" fill="rgba(255,255,255,0.08)" stroke="#e8f5c8" strokeWidth="2" strokeDasharray="6 5" />
      <text x={x} y={y + 4} textAnchor="middle" fontSize="11" fontWeight="800" fill="#f0ffd6" fontFamily="Fredoka, sans-serif">{`+ ${label}`}</text>
    </g>
  );
}

export const FarmWorld = ({ grid, plots, unlocked, now, onPlotClick, fx, decor, unlockCost, onDecorClick }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const trees = Math.min(decor.tree || 0, TREE_SPOTS.length);
  const left = tileTop(grid - 1, 0);
  const right = tileTop(0, grid - 1);
  const bottom = tileTop(grid - 1, grid - 1);
  const L = [left[0] - TW / 2 - 14, left[1] + TH / 2 + 22];
  const B = [bottom[0], bottom[1] + TH + 30];
  const R = [right[0] + TW / 2 + 14, right[1] + TH / 2 + 22];
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full h-auto block select-none" data-testid="valley-iso-farm">
      <defs>
        <radialGradient id="grass" cx="50%" cy="45%" r="75%">
          <stop offset="0%" stopColor="#7ed957" />
          <stop offset="60%" stopColor="#5cbf45" />
          <stop offset="100%" stopColor="#3f9a34" />
        </radialGradient>
        <linearGradient id="soil" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8a5a33" />
          <stop offset="100%" stopColor="#6b4325" />
        </linearGradient>
        <filter id="glow" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        <filter id="glowStrong" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="6" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        <pattern id="grassDots" width="38" height="22" patternUnits="userSpaceOnUse">
          <path d="M6 14 q2 -6 4 -8 M24 8 q2 -5 3 -7" stroke="#4aa83a" strokeWidth="1.6" fill="none" opacity="0.6" />
        </pattern>
      </defs>
      <rect width={VW} height={VH} fill="url(#grass)" />
      <rect width={VW} height={VH} fill="url(#grassDots)" />
      <path d={`M0 ${VH - 40} Q 250 ${VH - 90} 520 ${VH - 30} T ${VW} ${VH - 70} L ${VW} ${VH} L 0 ${VH} Z`} fill="#c9a66b" opacity="0.55" />
      {FLOWERS.map(([x, y, c]) => <Flower key={`${x}-${y}`} x={x} y={y} c={c} />)}
      {TUFTS.map(([x, y], k) => <GrassTuft key={`${x}-${y}`} x={x} y={y} delay={k * 0.3} />)}

      {decor.barn ? <Barn x={150} y={250} /> : <DecorSpot x={150} y={235} label="Barn" onClick={() => onDecorClick("barn")} tid="decor-spot-barn" />}
      {Array.from({ length: plots.length }).map((_, i) => (
        <Plot key={i} i={i} grid={grid} plot={plots[i]} unlocked={unlocked[i]} now={now} onClick={onPlotClick} unlockCost={unlockCost}
          hover={hoverIdx === i} setHover={setHoverIdx} />
      ))}
      {decor.fence ? (
        <g data-testid="decor-fence"><FenceLine from={L} to={B} /><FenceLine from={B} to={R} /></g>
      ) : (
        <DecorSpot x={B[0]} y={B[1] + 10} label="Fence" onClick={() => onDecorClick("fence")} tid="decor-spot-fence" />
      )}
      {decor.pond ? <g data-testid="decor-pond"><Pond x={835} y={585} /></g> : <DecorSpot x={835} y={585} label="Pond" onClick={() => onDecorClick("pond")} tid="decor-spot-pond" />}
      {TREE_SPOTS.map(([x, y, s], k) => (k < trees
        ? <g key={k} data-testid={`decor-tree-${k}`}><Tree x={x} y={y} s={s} delay={k * 0.7} /></g>
        : k === trees ? <DecorSpot key={k} x={x} y={y - 10} label="Tree" onClick={() => onDecorClick("tree")} tid="decor-spot-tree" /> : null))}

      {Array.from({ length: plots.length }).map((_, i) => (
        <PlotOverlay key={i} i={i} grid={grid} plot={plots[i]} unlocked={unlocked[i]} now={now} hover={hoverIdx === i} />
      ))}
      <AnimatePresence>{fx.map((f) => <Fx key={f.id} fx={f} grid={grid} />)}</AnimatePresence>
    </svg>
  );
};
